<template>
  <teleport to="body">
    <transition name="progress-fade">
      <div v-if="isVisible" class="loading-progress-bar">
        <div class="progress-bar-track">
          <div class="progress-bar-fill"></div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';

// State
const isVisible = ref<boolean>(false);
const pendingRequests = ref<number>(0);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Show the progress bar
 * Debounced to only show if loading takes more than 200ms
 */
const showProgressBar = () => {
  pendingRequests.value++;
  
  log('LoadingProgressBar', 'showProgressBar', 'Pending requests:', pendingRequests.value);
  
  // Only start debounce timer on first request
  if (pendingRequests.value === 1 && !isVisible.value) {
    debounceTimer = setTimeout(() => {
      if (pendingRequests.value > 0) {
        isVisible.value = true;
        log('LoadingProgressBar', 'showProgressBar', 'Progress bar visible (debounce completed)');
      }
    }, 200); // Show only if request takes more than 200ms
  }
};

/**
 * Hide the progress bar
 * Only hides when all pending requests are complete
 */
const hideProgressBar = () => {
  pendingRequests.value = Math.max(0, pendingRequests.value - 1);
  
  log('LoadingProgressBar', 'hideProgressBar', 'Pending requests:', pendingRequests.value);
  
  // Hide when all requests complete
  if (pendingRequests.value === 0) {
    // Clear debounce timer if still pending
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    
    // Hide the progress bar
    isVisible.value = false;
    log('LoadingProgressBar', 'hideProgressBar', 'Progress bar hidden (all requests complete)');
  }
};

/**
 * Force show (for manual control)
 */
const forceShow = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  isVisible.value = true;
  log('LoadingProgressBar', 'forceShow', 'Progress bar force shown');
};

/**
 * Force hide (for manual control)
 */
const forceHide = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pendingRequests.value = 0;
  isVisible.value = false;
  log('LoadingProgressBar', 'forceHide', 'Progress bar force hidden');
};

// Event listeners
onMounted(() => {
  log('LoadingProgressBar', 'onMounted', 'Component mounted, setting up event listeners');
  
  eventBus.on('loading.show', showProgressBar);
  eventBus.on('loading.hide', hideProgressBar);
  
  // Support for manual control
  eventBus.on('loading.forceShow', forceShow);
  eventBus.on('loading.forceHide', forceHide);
});

onBeforeUnmount(() => {
  log('LoadingProgressBar', 'onBeforeUnmount', 'Cleaning up event listeners');
  
  // Clean up debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  
  // Remove event listeners
  eventBus.off('loading.show', showProgressBar);
  eventBus.off('loading.hide', hideProgressBar);
  eventBus.off('loading.forceShow', forceShow);
  eventBus.off('loading.forceHide', forceHide);
});
</script>

<style scoped>
.loading-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  pointer-events: none; /* Don't block interactions */
}

.progress-bar-track {
  width: 100%;
  height: 12px;
  background-color: rgba(59, 130, 246, 0.2); /* Light blue background */
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    #3b82f6 25%,
    #60a5fa 50%,
    #3b82f6 75%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: progress-slide 1.5s ease-in-out infinite;
  width: 100%;
}

@keyframes progress-slide {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Fade transition */
.progress-fade-enter-active,
.progress-fade-leave-active {
  transition: opacity 0.2s ease;
}

.progress-fade-enter-from,
.progress-fade-leave-to {
  opacity: 0;
}

.progress-fade-enter-to,
.progress-fade-leave-from {
  opacity: 1;
}
</style>

